import type {
  ComponentRef,
  ComponentProps,
  CSSProperties,
  MouseEventHandler,
  KeyboardEvent,
  KeyboardEventHandler,
  ReactNode,
  RefObject,
} from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useLanguageSelectorLanguages } from './hooks/useLanguageSelectorLanguages';
import { Popup, PopupButton, PopupContent, usePopup, type PopupContext } from '@dx-ui/osc-popup-v2';
import { LANGUAGE_CODE_NAMES } from './osc-language-code-names';
import { Link } from '@dx-ui/osc-link';
import classnames from 'classnames';
import { getKeyDownNavigation } from '@dx-ui/utilities-accessibility';
import { getSafeLanguage } from '@dx-ui/framework-react-query';
import { Dialog } from '@dx-ui/osc-dialog-v2';
import { Icon } from '@dx-ui/osc-icon';
import { Heading, Text } from '@dx-ui/global-ui-prism';
import { SkeletonLoader } from '@dx-ui/osc-skeleton-loader';
import {
  dispatchLanguageSwitchSelect,
  type OnLanguageSelection,
} from './language-selector-analytics';

export type LanguageSelectorPropsBase = Omit<
  Parameters<typeof useLanguageSelectorLanguages>[0],
  'removeUrlParams' | 'ctyhocn'
> & {
  buttonOptions?: {
    className?: string;
    styles?: CSSProperties;
    hoverStyles?: CSSProperties;
  };
  ctyhocn?: string;
  labelOptions?: {
    className?: string;
    styles?: CSSProperties;
  };
  locale: string;
  theme?: Exclude<CmsBrandComponentTheme, 'light'>;
  onLanguageSelection?: OnLanguageSelection;
  // analytics-compat-bridge:osc-non-target-consumers
  isLegacyLanguageSwitchAnalyticsEnabled?: boolean;
  popupOptions?: {
    removeUrlParams?: boolean;
    includeTitles?: boolean;
    contentClassName?: string;
    contentStyles?: CSSProperties;
  };
  wrapperClassName?: string;
};

export type LanguageSelectorProps = LanguageSelectorPropsBase & ComponentProps<'div'>;

export function LanguageSelector({
  appName = '',
  buttonOptions,
  ctyhocn = '',
  customRuleEngine,
  locale = 'en',
  labelOptions,
  popupOptions,
  wrapperClassName,
  urlOverrides,
  theme,
  onLanguageSelection,
  isLegacyLanguageSwitchAnalyticsEnabled = true,
  className,
  ...rest
}: LanguageSelectorProps) {
  const [selectedLanguage, setSelectedLanguage] = useState<{
    code: string;
    href: string;
  }>();
  const [buttonHover, setButtonHover] = useState(false);
  const { t } = useTranslation('osc-language-selector');
  const [modalOpen, setModalOpen] = useState(false);
  const popupButtonRef = useRef<ComponentRef<'button'>>(null);
  const modalButtonRef = useRef<ComponentRef<'button'>>(null);
  const linksContainerRef = useRef<ComponentRef<'div'>>(null);

  const {
    loading,
    allLanguages,
    popularLanguagesOnly,
    nonPopularLanguages,
    sortedFullSiteLanguages,
    sortedResOnlyLanguages,
  } = useLanguageSelectorLanguages({
    appName,
    customRuleEngine,
    ctyhocn,
    removeUrlParams: popupOptions?.removeUrlParams,
    urlOverrides,
  });

  const selectedLanguageName = selectedLanguage?.code
    ? LANGUAGE_CODE_NAMES[selectedLanguage.code as keyof typeof LANGUAGE_CODE_NAMES]
    : undefined;

  const selectedLanguageValue = useMemo(() => {
    if (!loading) {
      const loc = getSafeLanguage(locale) ?? '';
      return {
        code: loc,
        href: allLanguages.find((lang) => lang.value === loc)?.href || '',
      };
    }
    return null;
  }, [allLanguages, loading, locale]);

  useEffect(() => {
    if (selectedLanguageValue && !selectedLanguage?.code) {
      setTimeout(() => setSelectedLanguage(selectedLanguageValue), 0);
    }
  }, [selectedLanguageValue, selectedLanguage?.code]);

  const focusFirstLink = useCallback(() => {
    linksContainerRef.current?.querySelector('a')?.focus();
  }, []);

  const handleLinkKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>, initiatingRef: RefObject<HTMLButtonElement | null>) => {
      const elements = Array.from(linksContainerRef.current?.querySelectorAll('a') || []);
      const keyHandler = getKeyDownNavigation({
        elements,
        initiatingRef,
      });
      keyHandler(event);
    },
    []
  );

  const handleLanguageSwitch = useCallback(
    (languageCode: string) => {
      dispatchLanguageSwitchSelect({
        languageCode,
        onLanguageSelection,
        isLegacyLanguageSwitchAnalyticsEnabled,
      });
    },
    [isLegacyLanguageSwitchAnalyticsEnabled, onLanguageSelection]
  );

  return (
    <div className="text--sm sm:text--md flex gap-2" {...rest}>
      {loading ? (
        <SkeletonLoader className="h-7 w-24" variant="rectangular" />
      ) : (
        <>
          <Dialog
            className={classnames('w-full', wrapperClassName)}
            isOpen={modalOpen}
            onDismiss={() => setModalOpen(false)}
            dialogTrigger={
              <button
                type="button"
                ref={modalButtonRef}
                className={classnames(
                  'osc-nav-item-text hidden items-center lg:inline-flex',
                  buttonOptions?.className
                )}
                onClick={() => {
                  setModalOpen(true);
                }}
                style={{
                  ...buttonOptions?.styles,
                  ...(buttonHover ? buttonOptions?.hoverStyles : null),
                }}
                onMouseEnter={() => setButtonHover(true)}
                onMouseLeave={() => setButtonHover(false)}
              >
                <span
                  className={classnames('flex', labelOptions?.className)}
                  style={labelOptions?.styles}
                >
                  <Text as="span" className="font-semibold [font-size:inherit]">
                    {t('language')}:&nbsp;
                  </Text>
                  <Text as="span" className="flex items-center [font-size:inherit]">
                    {selectedLanguageName ?? null}
                  </Text>
                </span>
              </button>
            }
            title={t('selectYourLanguage')}
            size="3xl"
          >
            {!!sortedFullSiteLanguages.length && (
              <div className="flex flex-col gap-6 md:flex-row">
                <LanguageListSection
                  title={t('popularLanguages')}
                  languages={popularLanguagesOnly}
                  containerClassName="bg-[--ds-hilton-enterprise-off-white] md:w-1/4"
                  listClassName="divide-y divide-surface"
                  itemClassName="ps-2"
                  selectedLanguage={selectedLanguage}
                  linkProps={{
                    onClick: (langValue: string) => {
                      handleLanguageSwitch(langValue);
                    },
                    onKeyDown: (e) => handleLinkKeyDown(e, modalButtonRef),
                  }}
                />
                <LanguageListSection
                  title={t('allLanguages')}
                  languages={nonPopularLanguages}
                  listClassName="grid grid-cols-1 gap-2 md:grid-cols-4"
                  selectedLanguage={selectedLanguage}
                  linkProps={{
                    onClick: (langValue: string) => {
                      handleLanguageSwitch(langValue);
                    },
                    onKeyDown: (e) => handleLinkKeyDown(e, modalButtonRef),
                  }}
                />
              </div>
            )}
          </Dialog>
          <Popup>
            <PopupContextState>
              {({ isOpen }) => (
                <>
                  <div className="text--sm flex items-baseline gap-2 lg:hidden">
                    <span
                      className={classnames('flex font-semibold', labelOptions?.className)}
                      style={labelOptions?.styles}
                    >
                      {t('language')}
                    </span>

                    {loading ? (
                      <SkeletonLoader className="h-7 w-24" variant="rectangular" />
                    ) : (
                      <PopupButton
                        ref={popupButtonRef}
                        className={classnames('inline-flex items-center', buttonOptions?.className)}
                        onArrowDownWhenOpen={focusFirstLink}
                        style={{
                          ...buttonOptions?.styles,
                          ...(buttonHover ? buttonOptions?.hoverStyles : null),
                        }}
                        onMouseEnter={() => setButtonHover(true)}
                        onMouseLeave={() => setButtonHover(false)}
                      >
                        <span className="sr-only">{t('language')}</span>
                        {selectedLanguage?.code ? (
                          <Text as="span" size="sm">
                            {selectedLanguageName}
                          </Text>
                        ) : null}
                        <Icon
                          name="arrowhead-small-down"
                          className={classnames({
                            hidden: isOpen,
                          })}
                        />
                        <Icon
                          name="arrowhead-small-up"
                          className={classnames({
                            hidden: !isOpen,
                          })}
                        />
                      </PopupButton>
                    )}
                  </div>
                  {loading ? null : (
                    <PopupContent
                      // eslint-disable-next-line tailwindcss/no-custom-classname
                      className={classnames('exclude-focus', popupOptions?.contentClassName)}
                      popupStyles={{
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        ...popupOptions?.contentStyles,
                      }}
                    >
                      <div
                        ref={linksContainerRef}
                        className="OneLinkKeepLinks flex min-w-[192px] flex-col gap-2"
                      >
                        {sortedFullSiteLanguages.length ? (
                          <div>
                            {popupOptions?.includeTitles ? (
                              <p className="mb-1 border-b border-surface pb-2 font-bold">
                                {t('selectYourLanguage')}
                              </p>
                            ) : null}
                            <ul className="divide-y divide-surface">
                              {sortedFullSiteLanguages.map((lang) => (
                                <ListItem
                                  key={lang.value}
                                  label={lang.label}
                                  language={lang.value}
                                  linkProps={{
                                    onClick: () => {
                                      handleLanguageSwitch(lang.value);
                                    },
                                    onKeyDown: (e) => handleLinkKeyDown(e, popupButtonRef),
                                    url: lang.href,
                                  }}
                                  selectedLanguage={selectedLanguage}
                                  showLanguageModal={false}
                                />
                              ))}
                            </ul>
                          </div>
                        ) : null}
                        {sortedResOnlyLanguages.length ? (
                          <div>
                            {popupOptions?.includeTitles ? (
                              <p className="mb-1 border-b border-surface pb-2 font-bold">
                                {t('bookingOnly')}
                              </p>
                            ) : null}
                            <ul>
                              {sortedResOnlyLanguages.map((lang) => (
                                <ListItem
                                  key={lang.value}
                                  label={lang.label}
                                  language={lang.value}
                                  linkProps={{
                                    onClick: () => {
                                      handleLanguageSwitch(lang.value);
                                    },
                                    onKeyDown: (e) => handleLinkKeyDown(e, popupButtonRef),
                                    url: lang.href,
                                  }}
                                  selectedLanguage={selectedLanguage}
                                  showLanguageModal={false}
                                />
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                    </PopupContent>
                  )}
                </>
              )}
            </PopupContextState>
          </Popup>
        </>
      )}
    </div>
  );
}

type ListItemProps = {
  label: string;
  language: string;
  linkProps: Omit<ComponentProps<typeof Link>, 'onClick'> & {
    onClick?: MouseEventHandler<HTMLAnchorElement>;
  };
  className?: string;
  selectedLanguage?: { code: string; href: string };
  showLanguageModal?: boolean;
};

function ListItem({
  label,
  language,
  linkProps,
  className,
  selectedLanguage,
  showLanguageModal,
}: ListItemProps) {
  const { t } = useTranslation('osc-language-selector');

  return (
    <li
      key={`popup-${language}`}
      className={classnames('group flex size-full py-2', className, {
        'items-center px-1 py-2 hover:bg-[--ds-hilton-enterprise-off-white]': showLanguageModal,
        'bg-[--ds-hilton-enterprise-off-white]':
          showLanguageModal && selectedLanguage?.code === language,
      })}
      lang={language}
    >
      <bdi className={classnames(showLanguageModal && 'flex size-full')} dir="ltr">
        {showLanguageModal && selectedLanguage?.code === language ? (
          <Link
            rel="noopener noreferrer"
            {...linkProps}
            anchorClassName="flex size-full items-center"
            className={classnames(
              showLanguageModal &&
                'flex items-center no-underline transition-all group-hover:underline group-focus:underline'
            )}
          >
            {label}
            <span className="ms-2 flex items-center text-surface-interactive">
              <Icon name="check" size="md" />
              <span className="sr-only">{t('selected')}</span>
            </span>
          </Link>
        ) : (
          <Link
            rel="noopener noreferrer"
            {...linkProps}
            anchorClassName="flex size-full items-center"
            className={classnames(
              showLanguageModal &&
                'flex no-underline transition-all group-hover:underline group-focus:underline'
            )}
          >
            {label}
          </Link>
        )}
      </bdi>
    </li>
  );
}

type LanguageListSectionProps = {
  title?: string;
  languages: { label: string; value: string; href: string; opensNewTab: boolean }[];
  containerClassName?: string;
  listClassName?: string;
  itemClassName?: string;
  linkProps: {
    onClick?: (langValue: string) => void;
    onKeyDown?: KeyboardEventHandler<HTMLAnchorElement>;
  };
  selectedLanguage?: { code: string; href: string };
};

function LanguageListSection({
  title,
  languages,
  containerClassName,
  listClassName,
  itemClassName,
  linkProps,
  selectedLanguage,
}: LanguageListSectionProps) {
  return (
    <div className={classnames('p-2', containerClassName)}>
      {title && (
        <Heading level={3} size="xs" className="mb-1 pb-2 font-bold">
          {title}
        </Heading>
      )}
      <ul className={listClassName}>
        {languages.map((lang) => {
          const handleClick: MouseEventHandler<HTMLAnchorElement> = (_e) => {
            linkProps.onClick?.(lang.value);
          };
          return (
            <ListItem
              key={lang.value}
              label={lang.label}
              language={lang.value}
              linkProps={{
                ...linkProps,
                url: lang.href,
                onClick: handleClick,
              }}
              className={itemClassName}
              selectedLanguage={selectedLanguage}
              showLanguageModal={true}
            />
          );
        })}
      </ul>
    </div>
  );
}

function PopupContextState({ children }: { children: (args0: PopupContext) => ReactNode }) {
  const context = usePopup();
  return children(context);
}

export default LanguageSelector;
